import { Button, Flex, Heading, Image, Dialog, Text } from '@chakra-ui/react';
import robotHead from 'src/assets/images/robotHead.svg';
import { ReactNode } from 'react';
import CrossIcon from 'src/assets/icons/CrossIcon';
import colors from 'src/theme/colors';
import fonts from 'src/theme/fonts';

type PrimaryButton = 'yes' | 'no';

// We cannot nest a button inside another button, so instead style the Typeform button to look like our own buttons
interface YesNoDialogProps {
  open: boolean;
  dialogTitle: string;
  dialogText: string;
  noBtnText?: string;
  yesBtnText?: string;
  noHandler?: () => void;
  yesHandler?: () => void;
  primaryButton?: PrimaryButton;
  isDestructive?: boolean;
  disabled?: boolean;
  onCancel?: () => void;
  isRobotIcon?: boolean;
  closeOnInteractOutside?: boolean;
  trapFocus?: boolean;
  Footer?: ReactNode;
  Body?: ReactNode;
}

export default function YesNoDialog({
  open,
  dialogTitle,
  dialogText,
  noBtnText,
  yesBtnText,
  noHandler,
  yesHandler,
  onCancel,
  isDestructive = false,
  disabled = false,
  isRobotIcon = false,
  closeOnInteractOutside = true,
  trapFocus = false,
  primaryButton = 'yes',
  Footer,
  Body,
}: YesNoDialogProps) {
  function onDialogClose() {
    if (onCancel) {
      onCancel();
    } else if (noHandler) {
      noHandler();
    }
  }

  const yesButtonVariant = primaryButton === 'yes' ? 'primary' : 'tertiary';
  const noButtonVariant = primaryButton === 'no' ? 'primary' : 'tertiary';

  return (
    <Dialog.Root
      open={open}
      onOpenChange={e => !e.open && onDialogClose()}
      placement="center"
      scrollBehavior="inside"
      closeOnInteractOutside={closeOnInteractOutside}
      trapFocus={trapFocus}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header bg={colors.white} p="28px 30px 14px 30px" borderBottom="unset">
            <Dialog.CloseTrigger asChild>
              <Button
                visual="empty"
                onClick={onDialogClose}
                aria-label="close"
                height="40px"
                width="40px"
              >
                <CrossIcon />
              </Button>
            </Dialog.CloseTrigger>
            {isRobotIcon && <Image src={robotHead} />}
            <Heading as="h4" visual="dialogTitle" mt={isRobotIcon ? '8px' : '0'}>
              {dialogTitle}
            </Heading>
          </Dialog.Header>
          <Dialog.Body p="0 30px 18px">
            <Text size="p2" visual="lighter">
              {dialogText}
            </Text>
            {Body}
          </Dialog.Body>
          <Dialog.Footer p="0 30px 30px">
            <Flex flexFlow="column">
              <Flex columnGap="10px" flexDirection={primaryButton === 'no' ? 'row-reverse' : 'row'}>
                {yesBtnText && (
                  <Button
                    width="fit-content"
                    visual={isDestructive ? 'destructive' : yesButtonVariant}
                    onClick={yesHandler}
                    disabled={disabled}
                  >
                    {yesBtnText}
                  </Button>
                )}
                {noBtnText && (
                  <Button
                    visual={noButtonVariant}
                    fontFamily={fonts.bold}
                    width="fit-content"
                    onClick={noHandler}
                  >
                    {noBtnText}
                  </Button>
                )}
              </Flex>
              {Footer}
            </Flex>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
